import { MenuItem } from '@/models';
import { MenuItemType, ItemCustomization, PortionItemType, VariantMenuItem, GroupedItemSelectionOption, GroupedItemCustomization } from './menu';
import { UniqueValue } from '@/helpers';
import { MenuItemMetadata, MenuItemGroup, CalorieRange } from './metadata';

export interface MealBuilderItemSection<T extends MenuItem = MenuItem> {
  id: string;
  sortOrder?: number;
  title: MenuItemType | string;
  hidden: boolean;
  minQuantity: number | null;
  maxQuantity: number | null;
  maxCustomizations: number | null;
  showSelectNone: boolean | null;

  items: SelectableMenuItem<T>[];
}

export enum ItemCardCustomizationType {
  SelectableItem,
  Customization,
  Normal,
}

interface BaseItemCardCustomization {
  type: ItemCardCustomizationType | null;
  text: string;
  iconText: string | null;
  selected: boolean;
  hidden: boolean;
}

export type ItemCardCustomization = SelectableItemCardCustomization | MenuItemCardCustomization | NormalItemCardCustomization;

export interface SelectableItemCardCustomization extends BaseItemCardCustomization {
  customization?: GroupedItemCustomization;
  type: ItemCardCustomizationType.SelectableItem;
  item: SelectableMenuItem;
  portion?: PortionItemType;
}

export interface MenuItemCardCustomization extends BaseItemCardCustomization {
  type: ItemCardCustomizationType.Customization;
  customization: ItemCustomization;
}

export interface NormalItemCardCustomization extends BaseItemCardCustomization {
  type: ItemCardCustomizationType.Normal;
}

export enum ItemCardSelectionInfoKind {
  Single = 'single',
  Quantity = 'quantity',
}

export enum SelectableItemPortion {
  None = 'none',
  Selected = 'selected',
  Single = 'single',
  Half = 'half',
  Extra = 'extra',
}

export type ItemCardSelectionInfo = QuantityItemCardSelectionInfo | SingleItemCardSelectionInfo;

export interface SingleItemCardSelectionInfo {
  kind: ItemCardSelectionInfoKind.Single;
  selected: boolean;
}

export interface QuantityItemCardSelectionInfo {
  kind: ItemCardSelectionInfoKind.Quantity;
  quantity: number;
  maxQuantity: number;
}

export interface ItemSelectorDetails {
  item: SelectableMenuItem;
  metadata: MenuItemMetadata;
  thumbnailImageUrl: string | null;
  name: string;
  formattedPrice: string | null;
  calories: string | null;
  portion: SelectableItemPortion | null;
  selected: boolean;
  customizing: boolean;
  disabled: boolean;
  customizations: ItemCardCustomization[];
  quantity: number | null;
  type: ItemCardSelectionInfoKind;
  message: UniqueValue<string> | null;
  promo: string | null;
  tagline: string | null;
  customizeText: string;
  isGroupedItem: boolean;
  isItemAvailable: boolean | null;
  eligibleForDelivery: boolean | null;
  isItemExclusive: boolean;
  rewardsText?: string; // Text value from the 'rewards' MMD tag
  selectedText?: string;
}

export enum SelectableMenuItemSource {
  Variant,
  VariantContents,
  NonVariant,
}

export interface SelectableMenuItem<TItem extends MenuItem = MenuItem, TPortion extends MenuItem = MenuItem> {
  item: TItem;
  itemSource: SelectableMenuItemSource;
  selectionInfo: ItemCardSelectionInfo;
  customizations: ItemCardCustomization[];
  groupedItemSelectionOptions?: GroupedItemSelectionOption[];
  displayNameForGroupedItems?: string;
  groupedItemsCalorieRange?: CalorieRange;
  displayRangeFormatForGroupedItems?: boolean;
  customizeTextForGroupedItems?: string;
  defaultItemId?: string;
  portions?: SelectableMenuItem<TPortion>[];
  message: UniqueValue<string> | null;
}

export interface SafelySelectableMenuItem<TSelectionInfo extends ItemCardSelectionInfo> extends SelectableMenuItem {
  selectionInfo: TSelectionInfo;
}

export interface SafelySelectableVariantMenuItem<TSelectionInfo extends ItemCardSelectionInfo> extends SelectableVariantMenuItem {
  selectionInfo: TSelectionInfo;
}

export interface SelectableVariantMenuItem extends SelectableMenuItem<VariantMenuItem> {
  portions: SelectableMenuItem[];
  contents: SelectableMenuItem[];
}

export interface SingleEntreeTypeBuilderMode extends BaseBuilderMode {
  mode: BuilderModeKind.SingleEntreeType;

  // A type would be Bowl, Burrito, Queso, BottledWater, etc.
  entree: {
    type: string;

    // A filling is what it sounds like
    fillings: SelectableVariantMenuItem[];
  };

  sides: SelectableMenuItem[];
  drinks: SelectableMenuItem[];
}

export interface MultiselectSingleEntreeTypeBuilderMode extends BaseBuilderMode {
  mode: BuilderModeKind.MultiselectSingleEntreeType;

  entree: {
    virtualItemSection: MealBuilderItemSection;
    variants: SelectableVariantMenuItem[];
  };

  sides: SelectableMenuItem[];
  drinks: SelectableMenuItem[];
}

export interface ZeroVariantBuilderMode extends BaseBuilderMode {
  mode: BuilderModeKind.ZeroVariant;

  categories: { [type: string]: SelectableMenuItem[] };
}

interface BaseBuilderMode {
  mode: BuilderModeKind;
  group: MenuItemGroup;
}

export enum BuilderModeKind {
  Empty = 'empty',
  SingleEntreeType = 'single-entree',
  MultiselectSingleEntreeType = 'multiselect-single-entree',
  ZeroVariant = 'zero-variant',
}

export type BuilderMode = SingleEntreeTypeBuilderMode | MultiselectSingleEntreeTypeBuilderMode | ZeroVariantBuilderMode;

export type EntreeBuilderMode = SingleEntreeTypeBuilderMode | MultiselectSingleEntreeTypeBuilderMode;
